import { useEffect, useState } from 'react';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-line.svg';
import { useLocale } from '../../../context/NbLocalizationContext';
import { NbButton } from '../../../shared/components/button/Button';
import { NbIconButton } from '../../../shared/components/icon-button/IconButton';
import { useAuthorizationFlow } from '../../../shared/services/authorization/useAuthorizationFlow';
import classes from './AutoLogoutModal.module.scss';

import { LogoutReason } from '@eih-netbroker/common';
import { Config } from '../../../shared/config/config';
import { useLogout } from '../../../shared/services/authorization/useLogout';

export interface AutoLogoutModalProps {
  onCancel: (loggedOut: boolean) => void;
}

const warningTimeout = Math.round(Config.NetBroker.Timeouts.ForceLogout.WarningTimeout / 1000);

export const AutoLogoutModal = ({ onCancel }: AutoLogoutModalProps) => {
  const { translation } = useLocale();
  const { appAuth } = useAuthorizationFlow();
  const { logout } = useLogout();
  const [remainingTime, setRemainingTime] = useState(warningTimeout);

  useEffect(() => {
    if (remainingTime === 0) {
      // once we reach 0, we get logged out. check it in AuthContext.tsx
      return;
    }

    const interval = setTimeout(() => {
      setRemainingTime(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(interval);
  }, [remainingTime]);

  useEffect(() => {
    if (!appAuth.isAuthenticated) {
      setRemainingTime(0);
    }
  }, [appAuth]);

  const onLogout = () => {
    onCancel(true);
    logout(LogoutReason.InactivityTimeout);
  };

  return (
    <div className={classes.logout}>
      <div className={classes.logoutHeader}>
        <h4>{remainingTime > 0 ? translation.logoutModal.title : translation.logoutModal.titleAfterTimeout}</h4>
        <NbIconButton
          icon={<CloseIcon />}
          onClick={() => onCancel(false)}
        />
      </div>
      {remainingTime > 0 ? (
        <div className={classes.logoutContent}>
          <span className={classes.logoutRemainingTime}>0:{String(remainingTime).padStart(2, '0')}</span>
          <span dangerouslySetInnerHTML={{ __html: translation.logoutModal.text }}></span>
        </div>
      ) : (
        <div className={classes.logoutContent}>
          <span>{translation.logoutModal.textAfterTimeout}</span>
        </div>
      )}
      <div className={classes.logoutFooter}>
        <NbButton
          onClick={() => onCancel(false)}
          className={classes.logoutButton}
          color="secondary"
        >
          {translation.general.cancel}
        </NbButton>
        {remainingTime > 0 && (
          <NbButton
            className={classes.logoutButton}
            onClick={onLogout}
          >
            {translation.general.logout}
          </NbButton>
        )}
      </div>
    </div>
  );
};
